"use client";

import React from "react";
import { Snowflake } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const packagesData = [{
  title: "Single Session",
  price: "$75/hr",
  description: "Perfect for quick vocal tracking or instrument overdubs.",
  features: ["Access to Studio B tracking room", "Professional sound engineer", "High-end mic locker access", "Basic rough mix included"],
  isPopular: false,
  buttonText: "Book Session",
  link: "/ContactPage"
}, {
  title: "EP Package",
  price: "$1,200",
  description: "Comprehensive production for 3-5 tracks.",
  features: ["20 hours of studio time", "Full mixing and mastering", "Creative production consulting", "Priority scheduling", "Digital distribution ready files"],
  isPopular: true,
  buttonText: "Start Project",
  link: "/ContactPage"
}, {
  title: "Album Deal",
  price: "Custom",
  description: "The ultimate creative journey for full-length records.",
  features: ["Unlimited Studio A access", "Senior executive producer", "Full analog & digital hybrid mix", "Vinyl-ready mastering", "Behind-the-scenes media kit"],
  isPopular: false,
  buttonText: "Custom Quote",
  link: "/ContactPage"
}];
export default function Packages() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="239930" id="studio-rates" className="bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-5xl h-[600px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <h2 className="text-4xl md:text-5xl font-default font-bold mb-6 tracking-tighter">
            Studio Rates
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Professional sonic excellence tailored to your project scale. From 
            independent singles to major label full-length albums.
          </p>
        </div>

        <div ref={ref} className={cn("grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-8 items-center max-w-6xl mx-auto transition-all duration-1000 opacity-0 translate-y-8", isVisible && "opacity-100 translate-y-0")}>
          {packagesData.map((pkg, i) => <div data-index={i} key={i} className={cn("relative group h-full transition-all duration-500", pkg.isPopular ? "md:scale-110 z-10" : "z-0")}>
              {}
              {pkg.isPopular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
                  <Badge variant="outline" className="bg-background/80 backdrop-blur-md border-primary text-primary shadow-lg shadow-primary/20 px-4 py-1 text-xs tracking-widest font-bold uppercase rounded-full">
                    Most Popular
                  </Badge>
                </div>}

              <Card className={cn("h-full flex flex-col relative overflow-hidden transition-all duration-500", "bg-card/40 backdrop-blur-xl border-border", "group-hover:bg-card/60 group-hover:border-primary/50 group-hover:shadow-2xl group-hover:shadow-primary/10", pkg.isPopular ? "border-primary/40 shadow-xl shadow-primary/5" : "rounded-3xl")}>
                {}
                <div className="absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/5 pointer-events-none" />

                <CardHeader className="text-center pb-4 pt-8">
                  <CardTitle className="text-2xl font-default font-bold mb-2 tracking-tight">
                    {pkg.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground h-10 font-default">
                    {pkg.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-grow flex flex-col items-center pt-4">
                  <div className="mb-8 text-center">
                    <span className="text-4xl lg:text-5xl font-mono font-bold text-foreground tracking-tighter">
                      {pkg.price}
                    </span>
                  </div>

                  <ul className="w-full space-y-4">
                    {pkg.features.map((feature, fIndex) => <li data-index={fIndex} key={fIndex} className="flex items-start gap-3 text-sm text-muted-foreground group-hover:text-foreground transition-colors duration-300 font-default">
                        <Snowflake className="w-4 h-4 text-primary shrink-0 mt-0.5 opacity-60 group-hover:opacity-100 transition-opacity duration-300" />
                        <span className="leading-tight">{feature}</span>
                      </li>)}
                  </ul>
                </CardContent>

                <CardFooter className="pt-6 pb-8">
                  <Button asChild variant={pkg.isPopular ? "default" : "outline"} className={cn("w-full h-12 text-base font-bold transition-all duration-300 rounded-full", !pkg.isPopular && "bg-transparent border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary")}>
                    <Link to={pkg.link}>{pkg.buttonText}</Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}